import { motion } from "framer-motion";
import { Bot, Loader2, MessageSquareText, Sparkles } from "lucide-react";
import { springs } from "@/components/ui";

interface ChatEmptyStateProps {
  isProcessing: boolean;
  onQuickAction: (text: string) => void;
}

const QUICK_ACTIONS = [
  "Summarize this document",
  "What are the key findings?",
  "List compliance issues",
  "What are the main risks?",
];

function ProcessingState() {
  return (
    <motion.div
      className="flex flex-col items-center justify-center text-center px-6 py-12 gap-4"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={springs.smooth}
    >
      <div className="relative">
        <motion.div
          className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 2,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
        >
          <Loader2 className="h-6 w-6 text-accent animate-spin" />
        </motion.div>
        <motion.div
          className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
        >
          <Sparkles className="h-3 w-3 text-amber-500" />
        </motion.div>
      </div>
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-primary">
          Analyzing your document...
        </p>
        <p className="text-xs text-muted max-w-[240px]">
          The AI is processing your document. You'll be able to chat once it's
          ready.
        </p>
      </div>
    </motion.div>
  );
}

function ReadyState({
  onQuickAction,
}: {
  onQuickAction: (text: string) => void;
}) {
  return (
    <motion.div
      className="flex flex-col items-center text-center px-6 py-8 gap-5"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.smooth}
    >
      <div className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center">
        <Bot className="h-5 w-5 text-accent" />
      </div>
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-primary">
          Ask anything about this document
        </p>
        <p className="text-xs text-muted max-w-[260px]">
          Get summaries, find compliance issues, or explore the content with AI.
        </p>
      </div>

      <div className="w-full space-y-2 mt-1">
        <p className="text-[11px] font-medium text-muted uppercase tracking-wider">
          Suggestions
        </p>
        <div className="flex flex-col gap-1.5">
          {QUICK_ACTIONS.map((action, i) => (
            <motion.button
              key={action}
              type="button"
              onClick={() => onQuickAction(action)}
              className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg border border-border/60 bg-card/50 hover:bg-card hover:border-border text-left transition-all group cursor-pointer"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springs.snappy, delay: 0.1 + i * 0.05 }}
            >
              <MessageSquareText className="h-3.5 w-3.5 text-muted group-hover:text-accent shrink-0 transition-colors" />
              <span className="text-[13px] text-secondary group-hover:text-primary transition-colors">
                {action}
              </span>
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export function ChatEmptyState({
  isProcessing,
  onQuickAction,
}: ChatEmptyStateProps) {
  if (isProcessing) {
    return <ProcessingState />;
  }
  return <ReadyState onQuickAction={onQuickAction} />;
}
